import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { DollarSign, FlaskConical, Zap, Handshake } from "lucide-react";
import TiltCard from "./TiltCard";

const stats = [
  { number: "5+", label: "Products Delivered" },
  { number: "2", label: "CRM Platforms Built" },
  { number: "24/7", label: "System Uptime" },
  { number: "100%", label: "On-Time Delivery" },
];

const process = [
  {
    step: "01",
    title: "Discovery Call",
    description:
      "Free 30-min call to understand your business, goals, and what revenue outcome you need.",
  },
  {
    step: "02",
    title: "Proposal & Scope",
    description:
      "Clear proposal with timeline, deliverables, and fixed pricing — no surprises.",
  },
  {
    step: "03",
    title: "Build & Iterate",
    description:
      "Weekly updates, demo sessions, and your feedback built into every sprint.",
  },
  {
    step: "04",
    title: "Launch & Support",
    description:
      "Production deployment, handoff documentation, and ongoing support available.",
  },
];

const values = [
  {
    title: "Revenue First",
    description:
      "Every feature we build is evaluated by one metric: does it help you make money?",
    icon: DollarSign,
  },
  {
    title: "Research-Driven",
    description:
      "Grounded in data, analytics, and research methodology — not guesswork.",
    icon: FlaskConical,
  },
  {
    title: "Production Quality",
    description:
      "Not prototypes — fully deployed, tested, and scalable systems you can rely on.",
    icon: Zap,
  },
  {
    title: "Transparent Pricing",
    description:
      "Fixed project quotes with clear scope. No hidden fees, no scope creep without approval.",
    icon: Handshake,
  },
];

const About = () => {
  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true });

  return (
    <section id="about" className="section-padding bg-dark-300 relative overflow-hidden">
      <div className="absolute inset-0 bg-mesh-gradient opacity-30 pointer-events-none" />

      <div className="section-container relative z-10">
        <motion.div
          ref={ref}
          className="text-center mb-16"
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <span className="text-accent-violet font-semibold text-sm uppercase tracking-widest">
            Why Purpose Labs
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-bold mt-3 mb-5 gradient-text">
            IT & Research Excellence
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            We're an IT and research services company serving clients worldwide — building booking
            platforms, CRMs, lead gen sites, and AI agents with research-driven insights.
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
        >
          {stats.map((stat, i) => (
            <TiltCard key={i} glowColor="primary">
              <div className="glass-panel p-6 text-center">
                <div className="font-display text-3xl md:text-4xl font-bold text-white mb-1">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            </TiltCard>
          ))}
        </motion.div>

        {/* Story + Process */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 mb-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3 }}
          >
            <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-6">
              From Idea to Revenue-Generating Product
            </h3>
            <div className="space-y-4 text-gray-400 leading-relaxed">
              <p>
                Purpose Labs started by solving real business problems — building
                booking platforms for homestays, CRMs for media companies, and
                estimation tools for construction firms. Every project taught us
                what actually drives revenue for business owners.
              </p>
              <p>
                Today we help businesses grow with{" "}
                <strong className="text-primary-400">lead generation websites</strong>
                ,{" "}
                <strong className="text-accent-cyan">booking systems</strong>,{" "}
                <strong className="text-accent-violet">custom CRMs</strong>, and{" "}
                <strong className="text-accent-emerald">AI inbound call agents</strong>{" "}
                — backed by our IT and research capabilities.
              </p>
              <p>
                Competitive rates, remote delivery, and a track record of shipped
                products — not slide decks.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.4 }}
            className="space-y-4"
          >
            <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-6">
              How We Work
            </h3>
            {process.map((step, i) => (
              <motion.div
                key={step.step}
                className="glass-panel p-5 flex gap-4 items-start"
                initial={{ opacity: 0, x: 20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.4 + i * 0.1 }}
              >
                <span className="font-display text-2xl font-bold text-primary-500/50 flex-shrink-0">
                  {step.step}
                </span>
                <div>
                  <h4 className="font-semibold text-white mb-1">{step.title}</h4>
                  <p className="text-sm text-gray-400">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Values */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
        >
          <h3 className="font-display text-2xl md:text-3xl font-bold text-center text-white mb-10">
            Why Clients Choose Us
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => {
              const ValueIcon = value.icon;
              return (
              <TiltCard key={i} glowColor="primary">
                <div className="glass-panel-hover p-6 text-center h-full">
                  <div className="flex justify-center mb-4 text-primary-400">
                    <ValueIcon className="w-9 h-9" strokeWidth={1.75} aria-hidden />
                  </div>
                  <h4 className="font-display font-bold text-white mb-2">
                    {value.title}
                  </h4>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </TiltCard>
            );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
